import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Import welding product images
import smawElectrode from "../../assets/products/electrodes/smaw-electrodes.jpg";
import gmawWire from "../../assets/products/electrodes/gmaw-wires.jpg";
import fcawWire from "../../assets/products/electrodes/fcaw-wires.jpg";
import gtawRod from "../../assets/products/electrodes/gtaw-rods.jpg";
import sawElectrode from "../../assets/products/electrodes/saw-electrodes.jpg";
import specialElectrode from "../../assets/products/electrodes/special-electrodes.jpg";
import weldingProcess from "../../assets/products/electrodes/welding-process.jpg";
import electrodeApp from "../../assets/products/electrodes/electrode-application.jpg";

// Import cable images
import copperCable from "../../assets/products/cables/copper-welding-cable.jpg";
import aluminumCable from "../../assets/products/cables/aluminum-welding-cable.jpg";
import industrialCable from "../../assets/products/cables/industrial-cables.jpg";
import cableConnector from "../../assets/products/cables/cable-connectors.jpg";

// Import safety equipment images
import weldingGoggles from "../../assets/products/safety/welding-goggles.jpeg";
import weldingHelmet from "../../assets/products/safety/welding-helmet.jpg";
import weldingGloves from "../../assets/products/safety/welding-gloves.jpg";
import weldingJacket from "../../assets/products/safety/welding-jacket.jpg";
import safetySet from "../../assets/products/safety/safety-equipment-set.jpeg";

// Import welding machine images
import smawMachine from "../../assets/products/machines/smaw-machine.jpg";
import migMachine from "../../assets/products/machines/mig-machine.jpg";
import tigMachine from "../../assets/products/machines/tig-machine.jpg";
import multiprocessWelder from "../../assets/products/machines/multiprocess-welder.jpg";

// Import holder images
import insulatedHolder from "../../assets/products/holders/insulated-holder.jpg";
import heavyDutyHolder from "../../assets/products/holders/heavy-duty-holder.jpg";
import standardHolder from "../../assets/products/holders/standard-holder.jpg";

// Import additional equipment images
import additionalGloves from "../../assets/products/additional/welding-gloves.jpg";
import additionalConnectors from "../../assets/products/additional/cable-connectors.jpg";
import copperLugs from "../../assets/products/additional/copper-lugs.jpg";

// Import specialized equipment images
import dptSet from "../../assets/products/specialized/dpt-set.jpg";
import electrodeOven from "../../assets/products/specialized/electrode-oven.jpg";
import weldingGlass from "../../assets/products/specialized/welding-glass.jpg";

// Import tool images
import weldingTorch from "../../assets/products/tools/welding-torch.jpg";
import powerTools from "../../assets/products/tools/power-tools.jpg";

// Import placeholder image
import placeholderImage from "../../assets/placeholder.jpg";

// Update the images object to use imported images with correct key mapping
const images = {
  electrodes: {
    shielded: smawElectrode,
    gas: gmawWire,
    flux: fcawWire,
    gtaw: gtawRod,
    submerged: sawElectrode,
    special: specialElectrode,
    process: weldingProcess,
    application: electrodeApp
  },
  cables: {
    copper: copperCable,
    aluminum: aluminumCable,
    industrial: industrialCable,
    connectors: cableConnector
  },
  safety: {
    welding: weldingGoggles,
    helmet: weldingHelmet,
    gloves: weldingGloves,
    jacket: weldingJacket,
    fullSet: safetySet
  },
  machines: {
    shielded: smawMachine,
    gas: migMachine,
    gtaw: tigMachine,
    multiprocess: multiprocessWelder
  },
  holders: {
    insulated: insulatedHolder,
    heavy: heavyDutyHolder,
    standard: standardHolder
  },
  additional: {
    gloves: additionalGloves,
    connectors: additionalConnectors,
    lugs: copperLugs
  },
  specialized: {
    dpt: dptSet,
    electrode: electrodeOven,
    welding: weldingGlass
  },
  tools: {
    welding: weldingTorch,
    power: powerTools
  }
};

// Update the ProductImage component with better error handling and image mapping
const ProductImage = ({ src, alt, className }) => {
  const [imageError, setImageError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  const handleImageError = (e) => {
    console.error(`Failed to load image: ${src}`);
    console.error(`Image alt text: ${alt}`);
    console.error(`Full image path:`, e.target.src);
    setImageError(true);
    e.target.src = placeholderImage;
    e.target.alt = 'Product image not available';
  };

  const handleImageLoad = () => {
    console.log(`Successfully loaded image: ${src}`);
    setIsLoading(false);
  };

  // If src is undefined or null, use placeholder
  const imageSrc = src || placeholderImage;

  return (
    <div className={`relative  overflow-hidden rounded-xl shadow-lg ${className || ''} bg-gray-100`}>
      {isLoading && !imageError && (
        <div className="absolute inset-0 animate-pulse bg-gray-200" />
      )}
      <img
        src={imageSrc}
        alt={alt}
        className="w-full h-full object-contain transform hover:scale-105 transition-transform duration-500 relative z-10"
        onError={handleImageError}
        onLoad={handleImageLoad}
        style={{ minHeight: '200px' }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 z-20" />
      {imageError && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm z-30">
          Image not available
        </div>
      )}
    </div>
  );
};

// Helper function to get the correct image key based on title
const getImageKey = (title, type) => {
  const firstWord = title.split(' ')[0].toLowerCase();
  const mapping = {
    'shielded': 'shielded',
    'gas': 'gas',
    'flux-cored': 'flux',
    'gtaw': 'gtaw',
    'submerged': 'submerged',
    'special': 'special',
    'welding': 'welding',
    'copper': 'copper',
    'aluminum': 'aluminum',
    'industrial': 'industrial',
    'dpt': 'dpt',
    'electrode': 'electrode',
    'power': 'power'
  };
  console.log(`Getting image for title: ${title}, type: ${type}, key: ${mapping[firstWord] || firstWord}`);
  return mapping[firstWord] || firstWord;
};

const WeldingMaterials = () => {
  const electrodeTypes = [
    {
      title: "Shielded Metal Arc Welding (SMAW) Electrodes",
      subtitle: "Stick Electrodes",
      description: "These are coated electrodes used in manual arc welding. The coating provides shielding gases and slag to protect the weld.",
      icon: "⚡",
      color: "text-blue-600",
      types: [
        { name: "E6010", desc: "Deep penetration, cellulose-coated, used for pipeline welding" },
        { name: "E6011", desc: "Similar to E6010 but usable with AC and DC" },
        { name: "E6013", desc: "General-purpose electrode, produces smooth welds with medium penetration" },
        { name: "E7018", desc: "Low-hydrogen electrode, strong welds, good for structural steel" },
        { name: "E7024", desc: "High-deposition electrode, used for heavy fabrications" }
      ]
    },
    {
      title: "Gas Metal Arc Welding (GMAW) Electrodes",
      subtitle: "MIG Wires",
      description: "Used in MIG welding, these are bare or lightly coated solid wires that require shielding gas (CO₂, Argon, or mixed gases).",
      icon: "🔌",
      color: "text-green-600",
      types: [
        { name: "ER70S-3", desc: "General-purpose MIG wire for clean steel" },
        { name: "ER70S-6", desc: "Best for welding rusted or contaminated steel due to higher deoxidizers" },
        { name: "ER308L", desc: "Stainless steel MIG wire for 304 and 308-grade stainless steel" }
      ]
    },
    {
      title: "Flux-Cored Arc Welding (FCAW) Electrodes",
      subtitle: "",
      description: "These electrodes have a flux core that eliminates or reduces the need for external shielding gas.",
      icon: "💫",
      color: "text-purple-600",
      types: [
        { name: "E71T-1", desc: "Flux-cored wire with CO₂ or gas shielding, used in structural welding" },
        { name: "E71T-11", desc: "Self-shielded wire, ideal for outdoor welding without gas" }
      ]
    },
    {
      title: "Gas Tungsten Arc Welding (GTAW) Electrodes",
      subtitle: "TIG Rods",
      description: "TIG welding uses non-consumable tungsten electrodes with separate filler rods.",
      icon: "🔥",
      color: "text-yellow-600",
      types: [
        { name: "ER70S-2", desc: "Used for mild steel welding" },
        { name: "ER308L", desc: "Stainless steel welding rod for 304 and 308-grade stainless" },
        { name: "ER4043 & ER5356", desc: "Aluminum TIG rods" }
      ]
    },
    {
      title: "Submerged Arc Welding (SAW) Electrodes",
      subtitle: "",
      description: "Used with a flux blanket, these electrodes are continuous solid or cored wires.",
      icon: "⚙️",
      color: "text-red-600",
      types: [
        { name: "EM12K, EL8, EH14", desc: "Various types for mild and low-alloy steel applications" }
      ]
    },
    {
      title: "Special & Hardfacing Electrodes",
      subtitle: "",
      description: "These electrodes are designed for wear resistance and repair applications.",
      icon: "🛠️",
      color: "text-indigo-600",
      types: [
        { name: "E308L, E309L, E316L", desc: "Stainless steel electrodes" },
        { name: "EFeCr-A1", desc: "Hardfacing electrode for high wear and abrasion resistance" }
      ]
    }
  ];

  const fillerWireTypes = [
    {
      title: "Mild Steel Filler Wires",
      description: "Used for welding low-carbon steel in structural and general fabrication work.",
      types: [
        {
          category: "MIG (GMAW) Wires",
          items: [
            { name: "ER70S-3", desc: "General-purpose wire for clean steel surfaces" },
            { name: "ER70S-6", desc: "Contains more deoxidizers, suitable for welding rusty or dirty steel" },
            { name: "ER70S-2", desc: "Used in TIG welding for high-quality welds in critical applications" }
          ]
        }
      ]
    },
    {
      title: "Stainless Steel Filler Wires",
      description: "Used for corrosion-resistant applications in industries like food, chemical, and marine.",
      types: [
        {
          category: "MIG & TIG Wires",
          items: [
            { name: "ER308L", desc: "For welding 304 and 308 stainless steel" },
            { name: "ER309L", desc: "Used for welding dissimilar metals (mild steel to stainless steel)" },
            { name: "ER316L", desc: "For 316 stainless steel, offering better corrosion resistance" }
          ]
        }
      ]
    }
  ];

  const weldingMachines = [
    {
      title: "Shielded Metal Arc Welding (SMAW) Machines",
      subtitle: "Stick Welders",
      description: "Used for manual arc welding with coated electrodes. Ideal for construction, maintenance, and pipeline welding.",
      features: [
        "Works with AC, DC, or both (AC/DC)",
        "Simple and portable",
        "Can weld in outdoor conditions (even on rusty metal)"
      ],
      types: [
        { name: "Transformer-based", desc: "Heavy-duty, long lifespan" },
        { name: "Inverter-based", desc: "Lightweight, energy-efficient, better arc control" }
      ]
    },
    {
      title: "Gas Metal Arc Welding (GMAW) Machines",
      subtitle: "MIG Welders",
      description: "Used for MIG and MAG welding. Ideal for fabrication, automotive, and industrial welding.",
      features: [
        "Uses a continuous wire feeder",
        "Requires shielding gas (CO₂, Argon, or mixed gases)",
        "Fast and easy to use, suitable for thin to medium-thickness metals"
      ],
      types: [
        { name: "Standard MIG Welder", desc: "Uses solid wire and external gas" },
        { name: "Synergic MIG Welder", desc: "Auto-adjusts parameters for ease of use" },
        { name: "Pulse MIG Welder", desc: "Reduces spatter, good for aluminum and stainless steel" }
      ]
    },
    {
      title: "Gas Tungsten Arc Welding (GTAW) Machines",
      subtitle: "TIG Welders",
      description: "Used for high-precision welding on stainless steel, aluminum, and exotic metals.",
      features: [
        "Uses non-consumable tungsten electrode",
        "Requires inert gas (Argon/Helium) for shielding",
        "Provides clean and precise welds"
      ],
      types: [
        { name: "DC TIG Welder", desc: "Suitable for steel, stainless steel, and titanium" },
        { name: "AC/DC TIG Welder", desc: "Needed for aluminum welding" },
        { name: "Pulse TIG Welder", desc: "Used for thin materials to reduce heat input" }
      ]
    }
  ];

  const weldingHolders = [
    {
      title: "Insulated Electrode Holders",
      description: "These holders have fully insulated handles for better safety and heat resistance.",
      features: [
        "Prevents accidental electric shocks",
        "Reduces heat transfer to the welder's hand",
        "Common in high-voltage welding environments"
      ],
      types: [
        { name: "Twist Lock Holders", desc: "Secure the electrode by twisting" },
        { name: "Lever Lock Holders", desc: "Use a spring-loaded lever for quick electrode replacement" }
      ]
    },
    {
      title: "Non-Insulated Electrode Holders",
      description: "These are bare metal holders without insulation. Used in heavy-duty welding applications where insulation is not a primary concern.",
      features: [
        "Conducts high amperage without overheating",
        "Not recommended for wet or humid conditions",
        "Used in industrial and high-heat environments"
      ]
    },
    {
      title: "Heavy-Duty Electrode Holders",
      description: "Designed for high-amperage welding (600A and above), used in industrial fabrication and shipbuilding.",
      features: [
        "Made of copper alloy for better conductivity",
        "Handles high heat without degradation",
        "Used in continuous welding operations"
      ]
    }
  ];

  const weldingCables = [
    {
      title: "Copper Welding Cables",
      description: "Welding copper cables are used to transmit electrical current from the welding machine to the electrode holder (for stick welding) or the welding torch (for MIG/TIG welding).",
      icon: "🔌",
      color: "text-orange-600",
      features: [
        "High Conductivity: Made of pure copper strands for low electrical resistance and efficient current flow",
        "Flexibility: Designed with multiple fine copper strands to be highly flexible, allowing easy movement during welding",
        "Heat & Oil Resistance: The outer insulation is made of heat-resistant and oil-resistant rubber or PVC",
        "Durability: Resistant to abrasion, moisture, and chemicals, making it ideal for industrial use"
      ],
      selectionGuide: [
        "Consider the welding amperage – Higher amperage requires a thicker cable",
        "Check the length required – Longer distances need a larger size to prevent voltage drop",
        "Choose the right insulation – Rubber for flexibility & durability, PVC for cost-effectiveness",
        "Ensure pure copper material – Best conductivity and long-term reliability"
      ]
    },
    {
      title: "Aluminum Welding Cables",
      description: "Aluminum welding cables are used to carry electric current in welding applications. They are a lightweight alternative to copper welding cables, commonly used in shipbuilding, automotive, and aerospace industries.",
      icon: "⚡",
      color: "text-gray-600",
      advantages: [
        "Lightweight for easy handling",
        "Cost-effective compared to copper welding cables",
        "Resistant to corrosion due to an oxide layer on aluminum"
      ],
      disadvantages: [
        "Lower conductivity than copper (requires larger cable size)",
        "Less flexible, which can make installation difficult in tight spaces",
        "More prone to oxidation if not properly treated or insulated"
      ]
    }
  ];

  const safetyEquipment = [
    {
      title: "Welding Goggles",
      description: "Welding goggles are essential personal protective equipment (PPE) designed to protect the eyes from intense light, UV/IR radiation, sparks, and debris during welding.",
      icon: weldingGoggles,
      color: "text-blue-600",
      applications: "Commonly used in oxy-fuel welding, cutting, brazing, and light-duty welding applications.",
      advantages: [
        "Lightweight and comfortable",
        "Portable alternative to full welding helmets",
        "Provides adequate protection for low-to-medium welding tasks"
      ],
      disadvantages: [
        "Not suitable for heavy-duty arc welding",
        "Limited coverage compared to welding helmets",
        "Can fog up if not properly ventilated"
      ]
    },
    {
      title: "Welding Helmets",
      description: "A welding helmet is essential personal protective equipment (PPE) used to shield the face, eyes, and neck from sparks, heat, intense light, and harmful UV/IR radiation generated during welding.",
      icon: safetySet,
      color: "text-red-600",
      applications: "Critical safety item for welders working with processes like MIG, TIG, Stick, and Arc welding.",
      features: [
        "Full face and neck protection",
        "Auto-darkening lens options available",
        "Adjustable headgear for comfort",
        "Various shade options for different welding processes"
      ]
    }
  ];

  const additionalEquipment = [
    {
      title: "Welding Hand Gloves",
      description: "Welding hand gloves are essential personal protective equipment (PPE) designed to protect welders' hands from heat, sparks, molten metal, and electrical hazards.",
      icon: "🧤",
      color: "text-yellow-600",
      features: [
        "Heat and flame resistant material",
        "Electrical insulation protection",
        "Durable construction for extended use",
        "Comfortable fit for precise control"
      ]
    },
    {
      title: "Cable Connectors",
      description: "Cable connectors are essential components in welding setups, providing a secure and efficient electrical connection between welding cables, machines, and electrode holders.",
      icon: "🔌",
      color: "text-blue-600",
      features: [
        "High conductivity materials",
        "Secure locking mechanisms",
        "Heat resistant design",
        "Easy installation and removal"
      ]
    },
    {
      title: "Copper Lugs",
      description: "Copper lugs are essential electrical components used to connect cables securely to electrical equipment, welding machines, batteries, and grounding systems. They ensure efficient conductivity and reliable mechanical fastening.",
      icon: "🔧",
      color: "text-orange-600",
      features: [
        "Superior electrical conductivity",
        "Corrosion resistant",
        "Various sizes available",
        "Strong mechanical connection"
      ]
    }
  ];

  const specializedEquipment = [
    {
      title: "DPT Set (Dye Penetrant Test)",
      description: "A Dye Penetrant Test (DPT) Set, also known as Liquid Penetrant Testing (LPT) Kit, is a non-destructive testing (NDT) method used to detect surface cracks, porosity, and defects in welding, metal fabrication, and industrial components.",
      applications: "Widely used in industries like welding, aerospace, automotive, and manufacturing to ensure structural integrity.",
      components: [
        "Penetrant dye",
        "Developer solution",
        "Cleaner/remover",
        "Application tools"
      ]
    },
    {
      title: "Electrode Drying Oven",
      description: "An Electrode Drying Oven is a specialized heating device used to store and recondition welding electrodes by removing moisture.",
      purpose: "This prevents welding defects such as porosity, hydrogen embrittlement, and cracking caused by moisture absorption in electrodes.",
      features: [
        "Temperature control system",
        "Multiple storage compartments",
        "Insulated construction",
        "Digital display and controls"
      ]
    },
    {
      title: "Welding Glass",
      description: "Welding Glass is a specialized protective lens used in welding helmets, hand shields, and goggles to protect welders' eyes from intense light, ultraviolet (UV), and infrared (IR) radiation produced during welding.",
      purpose: "It helps prevent arc eye (welders' flash) and long-term eye damage.",
      features: [
        "Various shade numbers available",
        "UV/IR protection",
        "Impact resistant",
        "Clear visibility"
      ]
    }
  ];

  const weldingTools = [
    {
      title: "Welding Torch",
      description: "A welding torch is a handheld tool used to control the heat, gas flow, and filler material during welding. It directs the flame or arc to the welding area, allowing precise metal joining.",
      types: [
        { name: "MIG Torch", desc: "For gas metal arc welding" },
        { name: "TIG Torch", desc: "For gas tungsten arc welding" },
        { name: "Oxy-Fuel Torch", desc: "For gas welding and cutting" }
      ]
    },
    {
      title: "Power Tools",
      description: "Power tools are electrically or pneumatically driven tools used in welding, metal fabrication, construction, and industrial applications to enhance efficiency, precision, and productivity.",
      categories: [
        {
          name: "Cutting Tools",
          items: ["Angle grinders", "Plasma cutters", "Metal shears"]
        },
        {
          name: "Surface Preparation",
          items: ["Wire brushes", "Sanders", "Needle scalers"]
        },
        {
          name: "Finishing Tools",
          items: ["Polishers", "Buffers", "Die grinders"]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-[160px]">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-gray-900 to-[#333333]">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:60px_60px]" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#D82F2F]/20 via-transparent to-[#D82F2F]/20" />

        <div className="container mx-auto px-4 relative">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl md:text-6xl font-bold text-white mb-6"
            >
              Welding Electrodes
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-white max-w-3xl mx-auto"
            >
              Welding electrodes are classified into different types based on their coating, composition, and application
            </motion.p>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 1.5, delay: 0.5 }}
              className="mt-16 h-1 max-w-xl mx-auto bg-gradient-to-r from-transparent via-gray-400 to-transparent"
            />
          </div>
        </div>
      </section>

      {/* Content Section - Electrodes */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="space-y-16">
            {electrodeTypes.map((type, idx) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              >
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <div className="mb-6">
                      <div className="flex items-center gap-4">
                        {/* <span className="text-6xl">{type.icon}</span> */}
                        <div>
                          <h2 className="text-3xl font-bold text-black mb-2">
                            {type.title}
                            {type.subtitle && (
                              <span className="text-xl text-black ml-3">
                                ({type.subtitle})
                              </span>
                            )}
                          </h2>
                          <div className="w-32 h-1 bg-gradient-to-r from-[#D82F2F] to-transparent"></div>
                        </div>
                      </div>
                    </div>
                    <p className="text-black text-lg mb-8">
                      {type.description}
                    </p>
                  </div>
                  <ProductImage
                    src={images.electrodes[getImageKey(type.title, 'electrodes')] || images.electrodes.process}
                    alt={type.title}
                    className="h-80"
                  />
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {type.types.map((item, itemIdx) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: (idx * 0.1) + (itemIdx * 0.1) }}
                      className="bg-gray-50 rounded-xl p-6 hover:shadow-md transition-all duration-300"
                    >
                      <h3 className="text-2xl font-semibold text-black mb-3">
                        {item.name}
                      </h3>
                      <p className="text-black text-lg">
                        {item.desc}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Filler Wires Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-center text-black mb-16"
          >
            Filler Wires
          </motion.h2>
          <div className="space-y-16">
            {fillerWireTypes.map((type, idx) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              >
                <div className="mb-6">
                  <h3 className="text-3xl font-bold text-black mb-2">
                    {type.title}
                  </h3>
                  <div className="w-32 h-1 bg-gradient-to-r from-[#D82F2F] to-transparent"></div>
                </div>
                <p className="text-black text-lg mb-8">{type.description}</p>
                {type.types.map((category, catIdx) => (
                  <div key={catIdx} className="mb-6">
                    <h4 className="text-xl font-semibold text-black mb-4">{category.category}</h4>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {category.items.map((item, itemIdx) => (
                        <motion.div
                          key={item.name}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: (idx * 0.1) + (itemIdx * 0.1) }}
                          className="bg-gray-50 rounded-xl p-6 hover:shadow-md transition-all duration-300"
                        >
                          <h5 className="text-xl font-semibold text-black mb-3">{item.name}</h5>
                          <p className="text-black">{item.desc}</p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Welding Machines Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-center text-black mb-16"
          >
            Welding Machines
          </motion.h2>
          <div className="space-y-16">
            {weldingMachines.map((machine, idx) => (
              <motion.div
                key={machine.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              >
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <div className="mb-6">
                      <h3 className="text-3xl font-bold text-black mb-2">
                        {machine.title}
                        {machine.subtitle && (
                          <span className="text-xl text-black ml-3">
                            ({machine.subtitle})
                          </span>
                        )}
                      </h3>
                      <div className="w-32 h-1 bg-gradient-to-r from-[#D82F2F] to-transparent"></div>
                    </div>
                    <p className="text-black text-lg mb-6">{machine.description}</p>
                  </div>
                  <ProductImage
                    src={images.machines[getImageKey(machine.title, 'machines')] || images.machines.multiprocess}
                    alt={machine.title}
                    className="h-80"
                  />
                </div>

                <div className="mb-8">
                  <h4 className="text-xl font-semibold text-black mb-4">Features</h4>
                  <ul className="list-disc list-inside space-y-2 text-black">
                    {machine.features.map((feature, featureIdx) => (
                      <li key={featureIdx}>{feature}</li>
                    ))}
                  </ul>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {machine.types.map((type, typeIdx) => (
                    <motion.div
                      key={type.name}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: (idx * 0.1) + (typeIdx * 0.1) }}
                      className="bg-gray-50 rounded-xl p-6 hover:shadow-md transition-all duration-300"
                    >
                      <h5 className="text-xl font-semibold text-black mb-3">{type.name}</h5>
                      <p className="text-black">{type.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Welding Holders Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-center text-black mb-16"
          >
            Welding Holders
          </motion.h2>
          <div className="space-y-16">
            {weldingHolders.map((holder, idx) => (
              <motion.div
                key={holder.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              >
                <div className="mb-6">
                  <h3 className="text-3xl font-bold text-black mb-2">
                    {holder.title}
                  </h3>
                  <div className="w-32 h-1 bg-gradient-to-r from-[#D82F2F] to-transparent"></div>
                </div>
                <p className="text-black text-lg mb-6">{holder.description}</p>

                <div className="mb-8">
                  <h4 className="text-xl font-semibold text-black mb-4">Features</h4>
                  <ul className="list-disc list-inside space-y-2 text-black">
                    {holder.features.map((feature, featureIdx) => (
                      <li key={featureIdx}>{feature}</li>
                    ))}
                  </ul>
                </div>

                {holder.types && (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {holder.types.map((type, typeIdx) => (
                      <motion.div
                        key={type.name}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: (idx * 0.1) + (typeIdx * 0.1) }}
                        className="bg-gray-50 rounded-xl p-6 hover:shadow-md transition-all duration-300"
                      >
                        <h5 className="text-xl font-semibold text-black mb-3">{type.name}</h5>
                        <p className="text-black">{type.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Welding Cables Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-center text-black mb-16"
          >
            Welding Cables
          </motion.h2>
          <div className="space-y-16">
            {weldingCables.map((cable, idx) => (
              <motion.div
                key={cable.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              >
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <div className="mb-6">
                      <h3 className="text-3xl font-bold text-black mb-2">
                        {cable.title}
                      </h3>
                      <div className="w-32 h-1 bg-gradient-to-r from-[#D82F2F] to-transparent"></div>
                    </div>
                    <p className="text-black text-lg mb-6">{cable.description}</p>
                  </div>
                  <ProductImage
                    src={images.cables[getImageKey(cable.title, 'cables')]}
                    alt={cable.title}
                    className="h-64 md:h-full"
                  />
                </div>

                {cable.features && (
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold text-black mb-4">Features</h4>
                    <ul className="list-disc list-inside space-y-2 text-black">
                      {cable.features.map((feature, featureIdx) => (
                        <li key={featureIdx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {cable.selectionGuide && (
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold text-black mb-4">Selection Guide</h4>
                    <ul className="list-disc list-inside space-y-2 text-black">
                      {cable.selectionGuide.map((guide, guideIdx) => (
                        <li key={guideIdx}>{guide}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {cable.advantages && (
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold text-black mb-4">Advantages</h4>
                    <ul className="list-disc list-inside space-y-2 text-black">
                      {cable.advantages.map((advantage, advIdx) => (
                        <li key={advIdx}>{advantage}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {cable.disadvantages && (
                  <div>
                    <h4 className="text-xl font-semibold text-black mb-4">Disadvantages</h4>
                    <ul className="list-disc list-inside space-y-2 text-black">
                      {cable.disadvantages.map((disadvantage, disIdx) => (
                        <li key={disIdx}>{disadvantage}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Equipment Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-center text-black mb-16"
          >
            Safety Equipment
          </motion.h2>
          <div className="space-y-16">
            {safetyEquipment.map((equipment, idx) => (
              <motion.div
                key={equipment.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              >
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  {/* Content on the left */}
                  <div>
                    <div className="mb-6">
                      <h3 className="text-3xl font-bold text-black mb-2">
                        {equipment.title}
                      </h3>
                      <div className="w-32 h-1 bg-gradient-to-r from-[#D82F2F] to-transparent"></div>
                    </div>
                    <p className="text-black text-lg mb-4">{equipment.description}</p>
                    <p className="text-black text-lg mb-6">{equipment.applications}</p>
                  </div>

                  {/* Image on the right */}
                  <div className="flex justify-end">
                    <img
                      src={equipment.icon}
                      alt={equipment.title}
                      className="h-96 w-96"
                    />
                  </div>
                </div>

                {equipment.features && (
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold text-black mb-4">Features</h4>
                    <ul className="list-disc list-inside space-y-2 text-black">
                      {equipment.features.map((feature, featureIdx) => (
                        <li key={featureIdx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {equipment.advantages && (
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold text-black mb-4">Advantages</h4>
                    <ul className="list-disc list-inside space-y-2 text-black">
                      {equipment.advantages.map((advantage, advIdx) => (
                        <li key={advIdx}>{advantage}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {equipment.disadvantages && (
                  <div>
                    <h4 className="text-xl font-semibold text-black mb-4">Disadvantages</h4>
                    <ul className="list-disc list-inside space-y-2 text-black">
                      {equipment.disadvantages.map((disadvantage, disIdx) => (
                        <li key={disIdx}>{disadvantage}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Equipment Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-center text-black mb-16"
          >
            Additional Equipment
          </motion.h2>
          <div className="space-y-16">
            {additionalEquipment.map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              >
                <div className="mb-6">
                  <h3 className="text-3xl font-bold text-black mb-2">
                    {item.title}
                  </h3>
                  <div className="w-32 h-1 bg-gradient-to-r from-[#D82F2F] to-transparent"></div>
                </div>
                <p className="text-black text-lg mb-6">{item.description}</p>

                <div className="mb-8">
                  <h4 className="text-xl font-semibold text-black mb-4">Features</h4>
                  <ul className="list-disc list-inside space-y-2 text-black">
                    {item.features.map((feature, featureIdx) => (
                      <li key={featureIdx}>{feature}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Equipment Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-center text-black mb-16"
          >
            Specialized Equipment
          </motion.h2>
          <div className="space-y-16">
            {specializedEquipment.map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              >
                <div className="mb-6">
                  <h3 className="text-3xl font-bold text-black mb-2">
                    {item.title}
                  </h3>
                  <div className="w-32 h-1 bg-gradient-to-r from-[#D82F2F] to-transparent"></div>
                </div>
                <p className="text-black text-lg mb-4">{item.description}</p>
                {item.purpose && (
                  <p className="text-black text-lg mb-6">{item.purpose}</p>
                )}
                {item.applications && (
                  <p className="text-black text-lg mb-6">{item.applications}</p>
                )}

                {item.components && (
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold text-black mb-4">Components</h4>
                    <ul className="list-disc list-inside space-y-2 text-black">
                      {item.components.map((component, compIdx) => (
                        <li key={compIdx}>{component}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {item.features && (
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold text-black mb-4">Features</h4>
                    <ul className="list-disc list-inside space-y-2 text-black">
                      {item.features.map((feature, featureIdx) => (
                        <li key={featureIdx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Welding Tools Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-center text-black mb-16"
          >
            Welding Tools
          </motion.h2>
          <div className="space-y-16">
            {weldingTools.map((tool, idx) => (
              <motion.div
                key={tool.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              >
                <div className="mb-6">
                  <h3 className="text-3xl font-bold text-black mb-2">
                    {tool.title}
                  </h3>
                  <div className="w-32 h-1 bg-gradient-to-r from-[#D82F2F] to-transparent"></div>
                </div>
                <p className="text-black text-lg mb-6">{tool.description}</p>

                {tool.types && (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {tool.types.map((type, typeIdx) => (
                      <motion.div
                        key={type.name}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: (idx * 0.1) + (typeIdx * 0.1) }}
                        className="bg-gray-50 rounded-xl p-6 hover:shadow-md transition-all duration-300"
                      >
                        <h5 className="text-xl font-semibold text-black mb-3">{type.name}</h5>
                        <p className="text-black">{type.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                )}

                {tool.categories && (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {tool.categories.map((category, catIdx) => (
                      <motion.div
                        key={category.name}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: (idx * 0.1) + (catIdx * 0.1) }}
                        className="bg-gray-50 rounded-xl p-6 hover:shadow-md transition-all duration-300"
                      >
                        <h5 className="text-xl font-semibold text-black mb-3">{category.name}</h5>
                        <ul className="list-disc list-inside space-y-2 text-black">
                          {category.items.map((item, itemIdx) => (
                            <li key={itemIdx}>{item}</li>
                          ))}
                        </ul>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
              Need Help Choosing the Right Electrode?
            </h2>
            <p className="text-black mb-8">
              Our welding experts are here to help you select the perfect electrode for your specific application
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 text-lg font-medium text-white bg-[#D82F2F] hover:bg-[#FF4D4D] rounded-full transition-all duration-300 transform hover:scale-105"
            >
              Contact Our Experts
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WeldingMaterials; 